import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Sparkles, FlaskConical, ArrowRight, CheckCircle2, Rocket } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

export default function OnboardingModal() {
  const { user, checkAuth } = useAuth();
  const { tf, lang, setLang } = useLang();
  const [step, setStep] = useState(1);
  const [activateDemo, setActivateDemo] = useState(true);
  const [loading, setLoading] = useState(false);

  // Open only when user.first_login is true
  const open = !!user?.first_login;
  if (!open) return null;

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { activate_demo: activateDemo });
      api.patch("/account/preferences", { language: lang }).catch(() => {});
      await checkAuth();
      toast.success(activateDemo ? tf("Mode démo activé") : tf("Bienvenue sur Athlynx"));
      // If demo enabled, hard reload to refresh all data fetches.
      if (activateDemo) window.location.reload();
    } catch (e) {
      toast.error(tf("Erreur onboarding"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-border bg-card dark:border-border dark:bg-surface"
        hideClose
        data-testid="onboarding-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Onboarding Athlynx</DialogTitle>
          <DialogDescription>
            {tfr("Configurez votre compte Athlynx en trois étapes.")}
          </DialogDescription>
        </VisuallyHidden.Root>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pt-6">
          {[1, 2, 3].map((i) => (
            <span
              key={i}
              className={`h-1 w-8 rounded-full transition-all duration-300 ${
                i === step
                  ? "bg-foreground dark:bg-white"
                  : i < step
                    ? "bg-foreground/40 dark:bg-white/40"
                    : "bg-foreground/15 dark:bg-secondary/60"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="p-8 pt-6 text-center" data-testid="onboarding-step-1">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-athlynx/15 text-athlynx">
              <Rocket className="h-6 w-6" />
            </div>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground">{tf("Bienvenue sur Athlynx")}</h2>
            <p className="mt-2 text-sm text-muted-foreground dark:text-white/60">
              {tf("Votre nouveau cockpit pour suivre joueurs, clubs et relances en un seul endroit.")}
              {!user?.billing_exempt && (
                <>{" "}{tf("Profitez de")} <span className="text-foreground dark:text-white font-medium">{tf("14 jours d'essai")}</span> {tf("sans carte bancaire.")}</>
              )}
            </p>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{tf("Langue de l'interface")}</p>
              <div className="inline-flex rounded-full border border-border overflow-hidden" role="radiogroup" aria-label={tf("Langue de l'interface")}>
                <button type="button" onClick={() => setLang("fr")} role="radio" aria-checked={lang === "fr"} data-testid="onboarding-lang-fr"
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${lang === "fr" ? "bg-athlynx text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {tfr("Français")}
                </button>
                <button type="button" onClick={() => setLang("en")} role="radio" aria-checked={lang === "en"} data-testid="onboarding-lang-en"
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${lang === "en" ? "bg-athlynx text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  English
                </button>
              </div>
            </div>
            <div className="mt-8 flex justify-center">
              <Button
                onClick={() => setStep(2)}
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97]"
                data-testid="onboarding-continue-1"
              >
                {tf("Continuer")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-8 pt-6" data-testid="onboarding-step-2">
            <h2 className="text-2xl font-semibold tracking-tight text-center text-foreground">
              {tf("Découvrir Athlynx avec des données de démo ?")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground dark:text-white/60 text-center">
              {tf("Vos vraies données restent protégées. Vous pouvez désactiver le mode démo à tout moment.")}
            </p>

            <div className="mt-6 space-y-3">
              <button
                onClick={() => setActivateDemo(true)}
                data-testid="onboarding-pick-demo"
                className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
                  activateDemo
                    ? "border-athlynx/50 bg-athlynx/5"
                    : "border-border bg-secondary/40 hover:bg-secondary dark:border-border dark:bg-secondary/30 dark:hover:bg-secondary/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-athlynx/15 text-athlynx inline-flex items-center justify-center">
                    <FlaskConical className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                      {tf("Activer le mode démo")}
                      <span className="rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-semibold">
                        {tf("RECOMMANDÉ")}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground dark:text-white/50 mt-0.5">
                      {tf("13 joueurs, 7 clubs, interactions et rappels prêts à explorer")}
                    </div>
                  </div>
                  {activateDemo && <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />}
                </div>
              </button>

              <button
                onClick={() => setActivateDemo(false)}
                data-testid="onboarding-pick-empty"
                className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
                  !activateDemo
                    ? "border-foreground/30 bg-secondary dark:border-white/30 dark:bg-secondary/50"
                    : "border-border bg-secondary/40 hover:bg-secondary dark:border-border dark:bg-secondary/30 dark:hover:bg-secondary/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-secondary text-foreground dark:bg-secondary/60 dark:text-white inline-flex items-center justify-center">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">{tf("Démarrer avec un compte vide")}</div>
                    <div className="text-xs text-muted-foreground dark:text-white/50 mt-0.5">
                      {tf("Importez vos joueurs ou ajoutez-les manuellement")}
                    </div>
                  </div>
                  {!activateDemo && <CheckCircle2 className="h-5 w-5 text-foreground dark:text-white" />}
                </div>
              </button>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent dark:text-white/60 dark:hover:text-white dark:hover:bg-secondary/60"
              >
                {tf("Retour")}
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97]"
                data-testid="onboarding-continue-2"
              >
                {tf("Continuer")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-8 pt-6 text-center" data-testid="onboarding-step-3">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="mt-5 text-2xl font-semibold tracking-tight text-foreground">{tf("Tout est prêt !")}</h2>
            <p className="mt-2 text-sm text-muted-foreground dark:text-white/60">
              {activateDemo
                ? tf("On charge vos données de démo et on vous redirige vers le dashboard.")
                : tf("Vous arrivez sur un dashboard vierge. Importez vos joueurs ou ajoutez-en un pour commencer.")}
            </p>
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent dark:text-white/60 dark:hover:text-white dark:hover:bg-secondary/60"
                disabled={loading}
              >
                {tf("Retour")}
              </Button>
              <Button
                onClick={finish}
                disabled={loading}
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97]"
                data-testid="onboarding-finish-btn"
              >
                {loading ? "…" : tf("Commencer")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
